import * as React from 'react'
import { FC, useCallback, useMemo, useState } from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { DeleteIcon } from '@sales-center/components'

import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'

const getStyles = (theme: Theme) => ({
  title: {
    textAlign: 'center',
    fontSize: '24px',
  },

  content: {
    textAlign: 'center',
    backgroundColor: '#f7f7f7',
    height: '140px',
  },

  textField: { backgroundColor: theme.palette.common.white },
  actions: { padding: theme.spacing(2) },

  loadingBox: {
    display: 'flex',
    height: '100%',
    margin: 'auto',
  },

  progInd: { margin: 'auto' },
})

type Props = {
  recordCount: number
  saving: boolean
  onSaveClick: () => void
  onCancelClick: () => void
}

export const DeleteConfirmation: FC<Props> = (props) => {
  const { saving, recordCount, onSaveClick, onCancelClick } = props

  const [confirm, setConfirm] = useState('')

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setConfirm(event.target.value),
    [],
  )

  const deleteConfirmed = useMemo(
    () => confirm.toLowerCase() === 'delete',
    [confirm],
  )

  const onKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) =>
      event.key === 'Enter' && deleteConfirmed && !saving && onSaveClick(),
    [deleteConfirmed, onSaveClick, saving],
  )
  const styles = getStyles(useTheme())

  return (
    <>
      <DialogTitle sx={styles.title}>
        <DeleteIcon />
        <br />
        Delete {recordCount} {recordCount === 1 ? 'Record' : 'Records'}
      </DialogTitle>
      <DialogContent dividers sx={styles.content}>
        {saving ? (
          <Box sx={styles.loadingBox}>
            <CircularProgress sx={styles.progInd} />
          </Box>
        ) : (
          <>
            <DialogContentText>
              You will permanently lose these records and it cannot be undone.
            </DialogContentText>

            <DialogContentText>
              Type &ldquo;Delete&rdquo; to confirm
            </DialogContentText>

            <TextField
              value={confirm}
              onChange={onChange}
              onKeyPress={onKeyPress}
              sx={styles.textField}
              autoFocus
              size="small"
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button
          onClick={onCancelClick}
          disabled={saving}
          color="primary"
          variant="outlined"
        >
          Cancel
        </Button>

        <Button
          onClick={onSaveClick}
          disabled={saving || !deleteConfirmed}
          color="primary"
          variant="contained"
        >
          Confirm
        </Button>
      </DialogActions>
    </>
  )
}
